import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Calendar, MapPin } from 'lucide-react';
import { SPORTS, FEDERATION } from '@/lib/api';

const HERO_LINES = ['Players', 'Sports', 'Federation.'];

const MANIFESTO = [
  { n: '01', title: 'Discipline over talent.', body: 'We build platforms where consistency beats one-time brilliance. Every fixture is a chance to sharpen the craft.' },
  { n: '02', title: 'Equal turf. Every athlete.', body: 'Championships across District, State and National levels — because opportunity should not depend on postcode.' },
  { n: '03', title: 'Zero-friction registration.', body: 'One form, one UPI payment, one Application Number. No paperwork queues. Instant tracking.' },
  { n: '04', title: 'Verified ID cards.', body: 'Every approved athlete gets a QR-verified digital ID — recognised at every PSF fixture across India.' },
];

const BG = 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxhdGhsZXRlJTIwc3ByaW50aW5nJTIwdHJhY2slMjBkYXJrfGVufDB8fHx8MTc4NDgwODI4NXww&ixlib=rb-4.1.0&q=85';

export default function LandingPage() {
  const [heroReady, setHeroReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setHeroReady(true), 100); return () => clearTimeout(t); }, []);

  return (
    <main className="bg-[#0A0A0A] text-white grain overflow-hidden" data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] pt-24 md:pt-32 pb-16 border-b border-white/10">
        <motion.div initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 0.35 }} transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})`, filter: 'grayscale(1) contrast(1.1)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-16 md:mb-24">
            <span data-testid="hero-eyebrow">◉ Reg. {FEDERATION.reg} — India</span>
            <span className="hidden md:inline">{FEDERATION.ministryLine}</span>
            <span>Season 2026</span>
          </div>

          <h1 className="font-display uppercase leading-[0.85] tracking-tighter text-[14vw] md:text-[11vw] lg:text-[9vw]" data-testid="hero-title">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span initial={{ y: '105%' }} animate={heroReady ? { y: '0%' } : { y: '105%' }} transition={{ duration: 1.05, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }} className="block will-change-transform">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="md:col-span-6 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              A national platform for students across 20 sports. Register, pay via UPI, and compete. From District to National. Zero paperwork.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }} className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end">
              <Link to="/register" className="group inline-flex items-center gap-3 bg-[#CCFF00] text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors" data-testid="hero-cta-register">
                Register Now <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform" />
              </Link>
              <Link to="/track" className="inline-flex items-center gap-3 border border-white/30 text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white/10 transition-colors" data-testid="hero-cta-track">
                Track Application <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }} className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8">
          {[['Athletes', '5,000'], ['Championships', '15'], ['Sports', '20'], ['Cities', '40+']].map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <span className="font-display text-4xl md:text-5xl">{v}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mt-2">{k}</span>
            </div>
          ))}
        </motion.div>
      </section>

      {/* 1st National Championship */}
      <section className="border-b border-white/10 py-20 md:py-28" data-testid="section-championship">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-2 w-2 bg-[#CCFF00] animate-pulse rounded-full" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#CCFF00]">Registrations Open</p>
            </div>
            <h2 className="font-display uppercase text-5xl md:text-7xl lg:text-[7vw] leading-[0.9] tracking-tighter">1<sup className="text-2xl md:text-4xl align-super">st</sup> National<br />Championship 2026</h2>
          </motion.div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Info icon={<Calendar className="h-4 w-4" />} label="Event Month" value="December 2026" />
            <Info icon={<MapPin className="h-4 w-4" />} label="Venue" value="To be announced" />
            <Info icon={<Calendar className="h-4 w-4" />} label="Last Date to Register" value="30 November 2026" />
          </div>

          <div className="mt-6 border border-white/10 bg-[#141414] p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Eligibility</p>
            <p className="text-white/80 text-sm md:text-base">Open to all school and college athletes across India. Age categories: U-14, U-17, U-19 and Open. Bring valid photo ID at the venue.</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-5 mb-2">Sports</p>
            <div className="flex flex-wrap gap-2">
              {SPORTS.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase tracking-widest border border-white/20 px-3 py-1.5 text-white/80 hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors" data-testid={`chip-${s.toLowerCase().replace(/[^a-z]/g, '')}`}>{s}</span>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 bg-[#CCFF00] text-black px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-white transition-colors" data-testid="champ-register">
              Register Now <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/live" className="inline-flex items-center gap-2 border border-white/20 px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-white/10" data-testid="champ-live">Live Scoreboard</Link>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-b border-white/10 py-10 md:py-14">
        <Marquee gradient={false} speed={40} className="overflow-hidden">
          {['Discipline', 'Craft', 'Opportunity', 'Fair Play', 'Grit', 'Verified', 'National', 'Playerza'].map((w, i) => (
            <span key={i} className="font-display uppercase text-[12vw] md:text-[8vw] text-stroke leading-none mx-8">{w} —</span>
          ))}
        </Marquee>
      </section>

      {/* Manifesto */}
      <section className="border-b border-white/10 py-24 md:py-40">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">The Manifesto</p>
              <h2 className="font-display uppercase text-5xl md:text-6xl leading-[0.9] tracking-tight">
                We don&apos;t sell trophies.<br />We <span className="text-[#CCFF00]">build</span> champions.
              </h2>
            </div>
          </div>
          <div className="md:col-span-8 md:col-start-6 space-y-20 md:space-y-28">
            {MANIFESTO.map((m, i) => (
              <motion.div key={m.n} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }} className="grid grid-cols-12 gap-6 border-t border-white/10 pt-8">
                <div className="col-span-3"><span className="font-display text-6xl md:text-7xl">{m.n}</span></div>
                <div className="col-span-9">
                  <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight mb-4">{m.title}</h3>
                  <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-lg">{m.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function Info({ icon, label, value }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-5" data-testid={`info-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-2">{icon}{label}</div>
      <div className="font-display text-2xl md:text-3xl mt-2">{value}</div>
    </div>
  );
}
